// The status filter beside the search (Figma 1049:39095): All, Open and Close with their counts.
// One filter for every view; only the helpdesk's open and closed for now.

import { useAtom } from 'jotai';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  SENTINEL_TICKET_STATUSES,
  SENTINEL_TICKET_STATUS_LABELS,
  sentinelTicketStatusAtom,
  type SentinelStatusCounts,
  type SentinelTicketStatus,
} from '@/hooks/use-sentinel-routing';
import { cn } from '@/lib/utils';
import { MENU_ITEM, MENU_LABEL_ROW, MENU_META, STATUS_PANEL } from './workspace-menu-styles';
import { FilterIcon, MenuCheck } from './workspace-menu-icons';

const ALL = 'all';

export function TicketStatusFilter({ counts }: { counts: SentinelStatusCounts | undefined }) {
  const [status, setStatus] = useAtom(sentinelTicketStatusAtom);
  const label = status ? SENTINEL_TICKET_STATUS_LABELS[status] : 'All';
  const rows: { value: SentinelTicketStatus | typeof ALL; label: string; count: number | undefined }[] = [
    { value: ALL, label: 'All', count: counts && counts.open + counts.closed },
    ...SENTINEL_TICKET_STATUSES.map((value) => ({ value, label: SENTINEL_TICKET_STATUS_LABELS[value], count: counts?.[value] })),
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Ticket status: ${label}`}
          title={`Showing ${label}`}
          className="size-[28px] shrink-0 rounded-[100px]"
        >
          <FilterIcon />
        </Button>
      </DropdownMenuTrigger>
      {/* Figma 1049:39222: its right edge 2px in from the button's, 5px below it; 20px wider than its 152px to fit the tick. */}
      <DropdownMenuContent
        align="end"
        alignOffset={2}
        sideOffset={5}
        aria-label="Ticket status"
        className={cn(STATUS_PANEL, 'w-[172px] min-w-0')}
      >
        {rows.map((row) => {
          const selected = (status ?? ALL) === row.value;
          return (
            <DropdownMenuItem
              key={row.value}
              role="menuitemradio"
              aria-checked={selected}
              onSelect={() => setStatus(row.value === ALL ? null : row.value)}
              className={MENU_ITEM}
            >
              <span className={MENU_LABEL_ROW}>
                <span className="font-medium">{row.label}</span>
                {row.count !== undefined && <span className={MENU_META}>{row.count}</span>}
              </span>
              {/* The same tick as the view switch, so the picked status shows. */}
              {selected && <MenuCheck />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
